<!-- TEMPLATE -->
<template>
  <div class="wrapper">
    <dean-dialog
        :title="title"
        :large="true"
        class="win-dialog-modal"
    >
      <slot name="load"></slot>

      <dean-notification :is-modal="true"></dean-notification>

      <finalizing-form v-if="currentServiceName" :serviceType="serviceType" :services="rows" :payloadData="payloadData" :key="formKey"></finalizing-form>

      <span slot="dean-dialog-button-group">
        <dean-button :danger="true" :dialog-button="true" @click.native="close()">Annuleren</dean-button>
        <dean-button :success="true" :dialog-button="true" @click.native="send()">Versturen</dean-button>
      </span>
    </dean-dialog>
  </div>
</template>

<!-- SCRIPT -->
<script lang="ts">
  import Vue from 'vue';
  import {Component, Prop} from 'vue-property-decorator';
  import axios from 'axios';
  import {
    // COMPONENTS
    deanDialog,
    warningIcon,
  } from '@deanone/dean-components';
  import deanButton from '@/components/deanComponents/deanButton.vue';
  import DeanNotification from '@/components/deanComponents/deanNotification.vue';
  import ToolsMixin from '@/mixins/toolsMixin';
  import {deanDialogBus} from '@/main';
  import FinalizingForm from '@/components/quote/finalizingForm.vue';
  import {mixins} from 'vue-class-component';
  import { validationMessage } from '@/mixins/validations';
  import {getErrorMessageFromQuoteCreationProcess} from "@/mixins/quoteCreationExceptionHandler";
  import {ServiceTypeEnum} from "@/enums/ServiceTypeEnum";

  @Component({
    components: {
      deanDialog,
      deanButton,
      DeanNotification,
      FinalizingForm,
      warningIcon,
    },
  })

  export default class WinDialog extends mixins(ToolsMixin) {
    // DATA
    @Prop() public payloadData!: {
      [key: string]: string,
    };
    public title = '';
    public serviceType: ServiceTypeEnum|null = null;
    public message = '';
    public data: any = null;
    public services: any = null;
    public rows: any = null;
    public currentServiceName: any = null;
    public baseUrl: string = '/-/trade/api/quote.finalize';
    public formKey: number = 0;

    // METHODS
    public close() {
      deanDialogBus.$emit('close');
    }

    public showDialog(next: boolean = false) {
      const services = this.groupByKeyValue(this.data, 'name');

      for (const serviceName of Object.keys(services)) {
        const service = services[serviceName];
        // select first if nothing is currently selected
        if (!this.currentServiceName) {
          this.currentServiceName = serviceName;
          this.title = serviceName;
          this.serviceType = service[0].serviceType;
          Vue.set(this, 'services', service);
        }

        if (next && this.currentServiceName === serviceName) {
          this.currentServiceName = null;
          next = false;
        }
      }
      if (!this.currentServiceName) {
        this.close();
      }
      this.rows = [];
      if (this.currentServiceName) {
        for (const service of services[this.currentServiceName]) {
          this.rows.push(service.rules);
        }
      }
    }

    public send() {
      this.$emit('setLoad', true);
      Vue.set(this, 'services', null);

      const transformedData = Object.fromEntries(
        Object.keys(this.data).map(key => {
          const item = this.data[key];
          return [
            item.id,
            {
              rules: Object.keys(item.rules).reduce((acc, ruleKey) => {
                acc[ruleKey] = {
                  value: item.rules[ruleKey].value,
                  rules: item.rules[ruleKey].rules,
                  fields: item.rules[ruleKey].fields,
                };
                return acc;
              }, {} as { [key: string]: { value: any; rules: any; fields: any; } })
            }
          ];
        })
      );

      const dataWasSent = Object.keys(transformedData).length > 0;

      axios.post(this.baseUrl, {
        id: this.payloadData.id,
        data: transformedData,
      })
        .then(
          (response) => {
            Vue.set(this, 'data', response.data.data);

            if (response.data.errors && response.data.errors.length !== 0) {
              const errorText: string|null = getErrorMessageFromQuoteCreationProcess(response.data);

              if (errorText) {
                this.$toast(validationMessage(errorText));
              }
            }

            this.formKey++;
            this.showDialog(true);
          },
          (error) => {
            if (error.response && error.response.data && error.response.data.errors) {
              const errorText: string|null = getErrorMessageFromQuoteCreationProcess(error.response.data);

              if (errorText) {
                this.$toast(validationMessage(errorText));
              } else if (dataWasSent) {
                this.$toast(this.$store.state.main.notification.messages.couldNotSendData);
              }
            } else if (dataWasSent) {
              this.$toast(this.$store.state.main.notification.messages.couldNotSendData);
            }
          },
        ).finally(() => {
          deanDialogBus.$emit('QuoteFinalize', {
            id: this.payloadData.id,
            dataWasSent,
          });

          this.$emit('setLoad', false);

          this.observeWinDialogHeight();
      });
    }

    public created() {
      this.$emit('setLoad', true);
      axios.post(
        this.baseUrl, {
        id: this.payloadData.id,
      }).then(
          (response) => {
            this.data = response.data.data;

            if (response.data.errors) {
              const errorText: string|null = getErrorMessageFromQuoteCreationProcess(response.data);

              if (errorText) {
                this.$toast(validationMessage(errorText));
              }
            }

            this.showDialog();
          },
        ).catch(
          (error) => {
            if (error.response.data.errors) {
              const errorText: string|null = getErrorMessageFromQuoteCreationProcess(error.response.data);

              if (errorText) {
                this.$toast(validationMessage(errorText));
              }
            }
            this.close();
          },
        ).finally(() => {
          deanDialogBus.$emit('QuoteFinalize', {
            id: this.payloadData.id,
            dataWasSent: false,
          });
          this.$emit('setLoad', false);

          this.observeWinDialogHeight();
      });
    }

    public observeWinDialogHeight() {
      const winDialogModal = document.querySelector('.win-dialog-modal') as HTMLElement | null;
      const winDialogModalSlot = document.querySelector('.win-dialog-modal .dean-dialog-slot') as HTMLElement | null;

      if (winDialogModal && winDialogModalSlot) {
        const resizeObserver = new ResizeObserver(function(this: WinDialog) {
          const modalHeight: number = winDialogModalSlot ? winDialogModalSlot.clientHeight : 0;

          if (modalHeight > 380) {
            winDialogModal.classList.remove('win-dialog-modal-small');
            winDialogModal.classList.add('win-dialog-modal-large');
          } else {
            winDialogModal.classList.remove('win-dialog-modal-large');
            winDialogModal.classList.add('win-dialog-modal-small');
          }
        });

        resizeObserver.observe(winDialogModalSlot);
      }
    }
  }
</script>

<!-- STYLE -->
<style lang="scss" scoped>

  // IMPORT
  @import '~@deanone/dean-components/src/scss/vars';

  :deep(.dialog.large) {
    .dean-dialog-container {
      min-width: 768px;
      max-width: 90%;
      width: 100%;
    }

    &.win-dialog-modal-large {
      .dean-dialog-content {
        .dean-dialog-slot {
          overflow-y: auto;
        }
      }
    }

    &.win-dialog-modal-small {
      .dean-dialog-content {
        .dean-dialog-slot {
          overflow-y: visible;
        }
      }
    }
  }

</style>
